import Gallery, { GalleryFillRow } from "components/Media/Gallery";
import { createDate, importAll, transformImages } from "utils";

import AdvancedPotionMakingEditionIV from "../../edition-IV";
import AdvancedPotionMakingPrincesCopy from "../../edition-II-prince's-copy";
import { Link } from "react-router-dom";
import ReplicaContent from "components/Replicas/ReplicaContent";
import ReplicaParagraph from "components/Replicas/ReplicaParagraph";
import ReplicaQuote from "components/Replicas/ReplicaQuote";
import ReplicaSpacer from "components/Replicas/ReplicaSpacer";
import ReplicaSubTitleAnchor from "components/Replicas/ReplicaSubTitleAnchor";
import { ReplicaVersionType } from "types";
import YouTubeVideo from "components/Media/YouTubeVideo";
import background from "./background.jpg";
import { routes } from "config";
import thumbnail from "./thumbnail.png";

let gallery = [];
let gallery_PrinceVsClean = [];

try {
  gallery = importAll(
    (require as any).context("./gallery", false, /\.(png|jpe?g|svg)$/),
  );
  gallery_PrinceVsClean = importAll(
    (require as any).context(
      "./Gallery - Clean vs Prince",
      false,
      /\.(png|jpe?g|svg)$/,
    ),
  );
} catch (e) {
  console.warn(e);
}

/* VERSION DATA */
export const VERSION_DATA: ReplicaVersionType = {
  isWIP: false,
  page: undefined,
  dateCreated: createDate({ day: 20, month: 5, year: 2026 }),
  datePublished: createDate({ day: 28, month: 6, year: 2026 }),
  version: "3",
  isSeries: false,
  background,
  thumbnail,
};

/* VERSION PAGE */
const Page = () => {
  return (
    <ReplicaContent>
      <ReplicaQuote source="Harry Potter and the Half-Blood Prince">
        Slughorn strode over to a corner cupboard and, after a moment’s
        foraging, emerged with two very battered-looking copies of Advanced
        Potion-Making by Libatius Borage, which he gave to Harry and Ron along
        with two sets of tarnished scales.
      </ReplicaQuote>

      <ReplicaParagraph>
        Making its appearance in Harry Potter and the Half-Blood Prince, the
        Advanced Potion-Making textbook was an integral part of the story.
        However, the version seen in the film was{" "}
        <Link
          to={
            "/" +
            routes.replicaDetail.slug.replace(
              ":id",
              AdvancedPotionMakingPrincesCopy.id,
            )
          }
          target="_blank"
        >
          Prince's heavily modified copy
        </Link>
        , filled with handwritten notes, corrections, spells, jinxes and various
        other annotations. In order to recreate Prince's copy, it was first
        necessary to design the underlying textbook itself — something that
        never actually appeared in the film on its own. This replica presents
        that clean, unmodified version of the book.
      </ReplicaParagraph>

      <ReplicaParagraph>
        While clean copies of Advanced Potion-Making can be seen in the hands of
        other students throughout the film, these are actually a newer
        in-universe edition of the textbook —{" "}
        <Link
          to={
            "/" +
            routes.replicaDetail.slug.replace(
              ":id",
              AdvancedPotionMakingEditionIV.id,
            )
          }
          target="_blank"
        >
          Edition IV
        </Link>
        , to be precise — featuring a different cover, dimensions, page edges
        and even slightly updated content. Much like real-life textbooks, newer
        editions would naturally include revisions, corrections and expanded
        material accumulated over the years. This replica instead represents the
        much older Edition II from the time when Prince attended Hogwarts
      </ReplicaParagraph>

      <ReplicaSpacer />
      <Gallery
        images={transformImages(gallery)}
        imagesPerRow={8}
        aspectRatio={0.75}
        imageOptions={[
          { index: 0, aspectRatio: 2 },
          { index: 1, aspectRatio: 1 },
          { index: 2, aspectRatio: 2 },
          { index: 3, aspectRatio: 1.5 },
          { index: 5, aspectRatio: 1.5 },
          { index: 7, aspectRatio: 2 },
          { index: 8, aspectRatio: 1.5 },
          { index: 11, aspectRatio: 2 },
          { index: 19, aspectRatio: 1.5 },
        ]}
        imageMaxHeight="180px"
        fillRowAt={GalleryFillRow.start}
      />
      <ReplicaSpacer />
      <YouTubeVideo src="https://www.youtube.com/watch?v=lOFMd6kkMq8" />
      <ReplicaSpacer />

      <ReplicaParagraph>
        The goal of this replica was to recreate a clean version of Advanced
        Potion Making – Edition II, as it would have originally appeared before
        any personal annotations or alterations were made by the Prince. That
        is, as it would look in its intended form, as it was when the prince
        acquired it. It was written and formatted as a real-life book, meaning
        that page numbers in the list of contents or in the Potions and
        Ingredients Index all match the actual pages, same as any page numbers
        mentioned in the text itself when referencing other parts of the book.
        On the outside, the book has only moderate damage to reflect a few weeks
        of usage and carrying around in a schoolbag, rather than the Prince’s
        heavily used copy.
      </ReplicaParagraph>

      <ReplicaSubTitleAnchor title={`"Clean" vs "Prince's" Copy`} />

      <ReplicaQuote source="Harry Potter and the Half-Blood Prince">
        Although the amount of homework they had been given prevented him from
        reading the whole of his copy of Advanced Potion-Making, he had skimmed
        through it sufficiently to see that there was barely a page on which the
        Prince had not made additional notes, not all of them concerned with
        potion-making. Here and there were directions for what looked like
        spells that the Prince had made up himself.
      </ReplicaQuote>

      <ReplicaParagraph>
        As I already mentioned, this book was created as a necessary step before
        designing the Prince’s Copy. Because of that, there is not much value in
        going into detail about the development process here, as most of it is
        already covered on the{" "}
        <Link
          to={
            "/" +
            routes.replicaDetail.slug.replace(
              ":id",
              AdvancedPotionMakingPrincesCopy.id,
            )
          }
          target="_blank"
        >
          Prince's Copy page
        </Link>
        . However, it is worth briefly noting the differences between the two
        versions. From the outside, this version is only lightly aged and
        distressed, intended to suggest a few weeks of being used and carried
        around in a schoolbag, whereas the Prince’s copy shows heavy wear and
        abuse. From the inside, the text is identical in both versions, with the
        (enormous) difference being that the Prince’s copy contains hundreds of
        handwritten notes, heavily altering the final look.
      </ReplicaParagraph>

      <ReplicaSpacer />
      <Gallery
        images={transformImages(gallery_PrinceVsClean)}
        imagesPerRow={2}
        imageOptions={[{ index: 0, aspectRatio: 2 }]}
        imageMaxHeight="400px"
        fillRowAt={GalleryFillRow.end}
      />
    </ReplicaContent>
  );
};

export default {
  ...VERSION_DATA,
  page: <Page />,
} as ReplicaVersionType;
